
  import { computed, defineComponent, onMounted, ref, nextTick } from 'vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import Multiselect from '@vueform/multiselect';
  import { useAbility } from '@casl/vue';
  import { useStore } from 'vuex';
  import { useI18n } from 'vue-i18n/index';
  import { Actions, Mutations } from '@/store/enums/StoreEnums';
  import FactorValuesCreating from '@/views/manage/IVE/estimator/FactorValuesCreating.vue';
  import FactorValuesEditing from '@/views/manage/IVE/estimator/FactorValuesEditing.vue';
  import { reinitializeComponents } from '@/core/plugins/keenthemes';
  import LandFactors from '@/store/enums/IVELandFactorsEnums';
  import * as Yup from 'yup';
  import { useRouter, useRoute } from 'vue-router';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import { ErrorMessage, Field, Form } from 'vee-validate';

  interface Factor {
    factorId: number;
    factorTitle: string;
  }

  export default defineComponent({
    name: 'land-estimator-cloning',
    components: {
      ErrorMessage,
      Field,
      Form,
      Multiselect,
      FactorValuesCreating,
      FactorValuesEditing,
    },
    async setup() {
      const router = useRouter();
      const route = useRoute();

      const { t, te } = useI18n();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      const store = useStore();
      const factorData = ref<Factor | undefined>();
      const editData = ref<Factor | undefined>();
      const comparing = ref<number>(0);
      const compareButtonRef = ref<null | HTMLButtonElement>(null);

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };
      const goBack = () => {
        router.back();
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('landingEstimatorClone'), [
          translate('ive'),
        ]);
        reinitializeComponents();
      });
      const areaSchema = Yup.object().shape({
        price: Yup.string()
          .required(() => translate('AREA_VALUE_IS_REQUIRED_FIELD'))
          .label('value'),
      });
      await store.dispatch(Actions.GET_ALL_AREAS_LIST, { type: 'all' });
      const areasCreate = computed(() => store.getters.allListAreas);
      const selectedAreaCreate = ref<number>(areasCreate.value[0].id);

      await store.dispatch(Actions.GET_ALL_IVE_AREAS);
      const areas = computed(() => store.getters.allIveAreasList);
      const selectedArea = route.params.id;

      const refreshValues = async () => {
        await store.dispatch(Actions.GET_IVE_AREA_ESTIMATIONS, {
          areaId: selectedArea,
          mutation: Mutations.SET_IVE_AREA_ESTIMATIONS,
          factors: LandFactors,
        });
      };

      await refreshValues();

      const handleAreaChange = async () => {
        if (factorData.value != undefined) {
          factorData.value.factorId = 0;
        }
        await nextTick();
        await refreshValues();
      };

      const factorsValues = computed(() => store.getters.areaOptionsValues);
      const numberOfValues = computed(() => {
        return factorsValues.value.reduce(
          (previousValue, currentValue) =>
            previousValue + currentValue.options.length,
          0
        );
      });

      const initCompare = async (val) => {
        ++comparing.value;
        if (compareButtonRef.value) {
          // eslint-disable-next-line
          compareButtonRef.value!.disabled = true;
          compareButtonRef.value.setAttribute('data-kt-indicator', 'on');
        }

        const mutation =
          comparing.value === 1
            ? Mutations.SET_COMPARE_IVE_AREA_ESTIMATIONS
            : Mutations.SET_SECOND_COMPARE_IVE_AREA_ESTIMATIONS;

        await store.dispatch(Actions.GET_IVE_AREA_ESTIMATIONS, {
          areaId: val,
          mutation,
          factors: LandFactors,
        });
        //Deactivate indicator
        compareButtonRef.value?.removeAttribute('data-kt-indicator');
        // eslint-disable-next-line
        compareButtonRef.value!.disabled = comparing.value === 2;
      };

      const resetComparing = () => {
        comparing.value = 0;
        // eslint-disable-next-line
        compareButtonRef.value!.disabled = false;
      };

      const primaryAreaClasses = computed(() => ({
        col: comparing.value === 0,
        'col-xl-6': comparing.value === 1,
        'col-xl-4': comparing.value === 2,
      }));

      const secondaryAreaClasses = computed(() => ({
        'd-none': comparing.value === 0,
        'col-xl-6': comparing.value === 1,
        'col-xl-4': comparing.value === 2,
      }));

      const tertiaryAreaClasses = computed(() => {
        return comparing.value === 2 ? 'col-xl-4' : 'd-none';
      });

      const onSubmitCreate = async (values) => {
        if (values.isSeaView == true) values.isSeaView = true;
        else values.isSeaView = false;
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        const payload = {
          areaId: selectedAreaCreate.value,
          price: Number(values.price),
          isSeaView: values.isSeaView,
          isCloned: true,
          cloneAreaId: selectedArea,
        };
        const createdArea = await store.dispatch(
          Actions.CREATE_IVE_CLONE_AREA,
          payload
        );

        const iveAreaId = createdArea.data.data.id;
        console.log(iveAreaId);
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];
        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_CREATED_AREA'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({
              name: 'land-estimator-listing-cloning',
              params: { id: iveAreaId },
            });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        tertiaryAreaClasses,
        primaryAreaClasses,
        secondaryAreaClasses,
        resetComparing,
        comparing,
        factorData,
        editData,
        factorsValues,
        compareFactorsValues: computed(
          () => store.getters.compareAreaOptionsValues
        ),
        secondCompareFactorsValues: computed(
          () => store.getters.secondCompareAreaOptionsValues
        ),
        numberOfValues,
        handleAreaChange,
        translate,
        selectedArea,
        selectedAreaCreate,
        areas,
        areasCreate,
        can,
        refreshValues,
        initCompare,
        compareButtonRef,
        goBack,
        areaSchema,
        onSubmitCreate,
        submitButton,
      };
    },
  });
